import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { BarChart3, Globe2, Users2, PieChart } from "lucide-react";

const T = {
  fr: {
    eyebrow: "Le paysage de l'IA",
    title: "Qui parle aux IA aujourd'hui ?",
    intro: "L'adoption des modèles de langage (ChatGPT, Claude, Gemini) dessine des tendances démographiques et géographiques marquées. Un aperçu — pour situer Le Cocon dans ce mouvement.",
    ageTitle: "Répartition par âge",
    ageIntro: "L'usage grand public est tiré par les jeunes générations ; l'usage professionnel intensif s'observe chez les profils expérimentés.",
    ageCols: ["Tranche d'âge", "Part d'utilisateurs", "Usage dominant"],
    ageRows: [
      { age: "18 – 24 ans", share: "~40–50 %", pct: 45, usage: "Études, rédaction, vulgarisation, créativité" },
      { age: "25 – 34 ans", share: "~25–30 %", pct: 27, usage: "Productivité, programmation, marketing" },
      { age: "35 – 44 ans", share: "~12–15 %", pct: 13, usage: "Gestion de projet, analyse, stratégie" },
      { age: "45 – 54 ans", share: "~7–10 %", pct: 8, usage: "Synthèse d'informations, assistance technique" },
      { age: "55 ans et +", share: "< 5 %", pct: 4, usage: "Découverte, requêtes ponctuelles" },
    ],
    geoTitle: "Répartition géographique",
    geoIntro: "En volume, les États-Unis et l'Inde concentrent la majorité des utilisateurs. Les pays émergents progressent le plus vite.",
    geoRows: [
      { label: "🇺🇸 États-Unis", val: "~15–20 %", pct: 18 },
      { label: "🇮🇳 Inde", val: "~10–13 %", pct: 11 },
      { label: "🇧🇷 Brésil", val: "~4–5 %", pct: 5 },
      { label: "🇬🇧 R.-U. / 🇩🇪 Allemagne", val: "~3–4 % chacun", pct: 4 },
      { label: "🇮🇩 Indonésie / 🇵🇭 Philippines", val: "En forte expansion", pct: 3 },
    ],
    geoNote: "Asie de l'Est (Chine, Japon, Corée du Sud) : forte consommation d'API et essor des modèles locaux (DeepSeek, Qwen).",
    genderTitle: "Genre & secteurs",
    genderStat: "55–60 %",
    genderLabel: "d'hommes parmi les utilisateurs réguliers — écart plus marqué sur les usages techniques (dev, data).",
    sectorsLabel: "Secteurs clés",
    sectors: ["Informatique & ingénierie logicielle (~90 % chez les devs)", "Éducation", "Marketing & communication", "Finance"],
    platTitle: "Parts de marché — grand public",
    platRows: [
      { name: "ChatGPT (OpenAI)", val: "~60 %", pct: 60 },
      { name: "Google Gemini", val: "~20–24 %", pct: 22 },
      { name: "Copilot & Claude", val: "~10–15 %", pct: 12 },
      { name: "Moteurs IA (Perplexity, DeepSeek…)", val: "~5–8 %", pct: 6 },
    ],
    disclaimer: "Estimations agrégées (2025–2026) — indicatives, à visée pédagogique.",
  },
  en: {
    eyebrow: "The AI landscape",
    title: "Who talks to AI today?",
    intro: "The adoption of language models (ChatGPT, Claude, Gemini) reveals strong demographic and geographic trends. A snapshot — to place The Cocoon within this shift.",
    ageTitle: "By age group",
    ageIntro: "Consumer use is driven by younger generations; heavy professional use appears among experienced profiles.",
    ageCols: ["Age group", "Share of users", "Dominant use"],
    ageRows: [
      { age: "18 – 24", share: "~40–50%", pct: 45, usage: "Studies, writing, learning, creativity" },
      { age: "25 – 34", share: "~25–30%", pct: 27, usage: "Productivity, coding, marketing" },
      { age: "35 – 44", share: "~12–15%", pct: 13, usage: "Project management, analysis, strategy" },
      { age: "45 – 54", share: "~7–10%", pct: 8, usage: "Information synthesis, technical support" },
      { age: "55+", share: "< 5%", pct: 4, usage: "Discovery, occasional queries" },
    ],
    geoTitle: "By geography",
    geoIntro: "By volume, the US and India hold most users. Emerging markets grow the fastest.",
    geoRows: [
      { label: "🇺🇸 United States", val: "~15–20%", pct: 18 },
      { label: "🇮🇳 India", val: "~10–13%", pct: 11 },
      { label: "🇧🇷 Brazil", val: "~4–5%", pct: 5 },
      { label: "🇬🇧 UK / 🇩🇪 Germany", val: "~3–4% each", pct: 4 },
      { label: "🇮🇩 Indonesia / 🇵🇭 Philippines", val: "Fast-growing", pct: 3 },
    ],
    geoNote: "East Asia (China, Japan, South Korea): heavy API usage and a surge of local models (DeepSeek, Qwen).",
    genderTitle: "Gender & sectors",
    genderStat: "55–60%",
    genderLabel: "men among regular users — a wider gap in technical use (dev, data).",
    sectorsLabel: "Key sectors",
    sectors: ["Software & engineering (~90% among devs)", "Education", "Marketing & communication", "Finance"],
    platTitle: "Consumer market share",
    platRows: [
      { name: "ChatGPT (OpenAI)", val: "~60%", pct: 60 },
      { name: "Google Gemini", val: "~20–24%", pct: 22 },
      { name: "Copilot & Claude", val: "~10–15%", pct: 12 },
      { name: "AI search (Perplexity, DeepSeek…)", val: "~5–8%", pct: 6 },
    ],
    disclaimer: "Aggregated estimates (2025–2026) — indicative, for educational purposes.",
  },
  es: {
    eyebrow: "El panorama de la IA",
    title: "¿Quién habla con la IA hoy?",
    intro: "La adopción de los modelos de lenguaje (ChatGPT, Claude, Gemini) revela fuertes tendencias demográficas y geográficas. Una panorámica — para situar a El Cocoon en este movimiento.",
    ageTitle: "Por grupo de edad",
    ageIntro: "El uso general lo impulsan las generaciones jóvenes; el uso profesional intensivo aparece en perfiles experimentados.",
    ageCols: ["Grupo de edad", "Cuota de usuarios", "Uso dominante"],
    ageRows: [
      { age: "18 – 24 años", share: "~40–50 %", pct: 45, usage: "Estudios, redacción, divulgación, creatividad" },
      { age: "25 – 34 años", share: "~25–30 %", pct: 27, usage: "Productividad, programación, marketing" },
      { age: "35 – 44 años", share: "~12–15 %", pct: 13, usage: "Gestión de proyectos, análisis, estrategia" },
      { age: "45 – 54 años", share: "~7–10 %", pct: 8, usage: "Síntesis de información, soporte técnico" },
      { age: "55 años y +", share: "< 5 %", pct: 4, usage: "Descubrimiento, consultas puntuales" },
    ],
    geoTitle: "Por geografía",
    geoIntro: "En volumen, EE. UU. e India concentran la mayoría de usuarios. Los países emergentes crecen más rápido.",
    geoRows: [
      { label: "🇺🇸 Estados Unidos", val: "~15–20 %", pct: 18 },
      { label: "🇮🇳 India", val: "~10–13 %", pct: 11 },
      { label: "🇧🇷 Brasil", val: "~4–5 %", pct: 5 },
      { label: "🇬🇧 R.U. / 🇩🇪 Alemania", val: "~3–4 % c/u", pct: 4 },
      { label: "🇮🇩 Indonesia / 🇵🇭 Filipinas", val: "En fuerte expansión", pct: 3 },
    ],
    geoNote: "Asia Oriental (China, Japón, Corea del Sur): fuerte consumo de API y auge de modelos locales (DeepSeek, Qwen).",
    genderTitle: "Género y sectores",
    genderStat: "55–60 %",
    genderLabel: "hombres entre los usuarios habituales — brecha mayor en usos técnicos (dev, datos).",
    sectorsLabel: "Sectores clave",
    sectors: ["Informática e ingeniería de software (~90 % entre devs)", "Educación", "Marketing y comunicación", "Finanzas"],
    platTitle: "Cuota de mercado — público general",
    platRows: [
      { name: "ChatGPT (OpenAI)", val: "~60 %", pct: 60 },
      { name: "Google Gemini", val: "~20–24 %", pct: 22 },
      { name: "Copilot y Claude", val: "~10–15 %", pct: 12 },
      { name: "Buscadores IA (Perplexity, DeepSeek…)", val: "~5–8 %", pct: 6 },
    ],
    disclaimer: "Estimaciones agregadas (2025–2026) — indicativas, con fines educativos.",
  },
  nl: {
    eyebrow: "Het AI-landschap",
    title: "Wie praat vandaag met AI?",
    intro: "De adoptie van taalmodellen (ChatGPT, Claude, Gemini) toont sterke demografische en geografische trends. Een momentopname — om de Cocon in deze beweging te plaatsen.",
    ageTitle: "Per leeftijdsgroep",
    ageIntro: "Consumentengebruik wordt gedreven door jongere generaties; intensief professioneel gebruik zie je bij ervaren profielen.",
    ageCols: ["Leeftijdsgroep", "Aandeel gebruikers", "Dominant gebruik"],
    ageRows: [
      { age: "18 – 24 jaar", share: "~40–50%", pct: 45, usage: "Studie, schrijven, uitleg, creativiteit" },
      { age: "25 – 34 jaar", share: "~25–30%", pct: 27, usage: "Productiviteit, programmeren, marketing" },
      { age: "35 – 44 jaar", share: "~12–15%", pct: 13, usage: "Projectbeheer, analyse, strategie" },
      { age: "45 – 54 jaar", share: "~7–10%", pct: 8, usage: "Informatie samenvatten, technische hulp" },
      { age: "55+ jaar", share: "< 5%", pct: 4, usage: "Ontdekken, incidentele vragen" },
    ],
    geoTitle: "Per geografie",
    geoIntro: "In volume hebben de VS en India de meeste gebruikers. Opkomende markten groeien het snelst.",
    geoRows: [
      { label: "🇺🇸 Verenigde Staten", val: "~15–20%", pct: 18 },
      { label: "🇮🇳 India", val: "~10–13%", pct: 11 },
      { label: "🇧🇷 Brazilië", val: "~4–5%", pct: 5 },
      { label: "🇬🇧 VK / 🇩🇪 Duitsland", val: "~3–4% elk", pct: 4 },
      { label: "🇮🇩 Indonesië / 🇵🇭 Filipijnen", val: "Sterk groeiend", pct: 3 },
    ],
    geoNote: "Oost-Azië (China, Japan, Zuid-Korea): veel API-gebruik en opkomst van lokale modellen (DeepSeek, Qwen).",
    genderTitle: "Gender & sectoren",
    genderStat: "55–60%",
    genderLabel: "mannen onder vaste gebruikers — grotere kloof bij technisch gebruik (dev, data).",
    sectorsLabel: "Belangrijke sectoren",
    sectors: ["Software & engineering (~90% onder devs)", "Onderwijs", "Marketing & communicatie", "Financiën"],
    platTitle: "Marktaandeel — consument",
    platRows: [
      { name: "ChatGPT (OpenAI)", val: "~60%", pct: 60 },
      { name: "Google Gemini", val: "~20–24%", pct: 22 },
      { name: "Copilot & Claude", val: "~10–15%", pct: 12 },
      { name: "AI-zoekmachines (Perplexity, DeepSeek…)", val: "~5–8%", pct: 6 },
    ],
    disclaimer: "Geaggregeerde schattingen (2025–2026) — indicatief, voor educatieve doeleinden.",
  },
};

const Bar = ({ pct, val, delay = 0 }) => (
  <div className="flex items-center gap-3">
    <div className="relative h-2 flex-1 rounded-full bg-white/5 overflow-hidden">
      <motion.div
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ background: "linear-gradient(90deg, hsl(38 95% 60%), hsl(28 90% 52%))" }}
        initial={{ width: 0 }}
        whileInView={{ width: `${Math.min(100, pct * 1.6)}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay, ease: "easeOut" }}
      />
    </div>
    <span className="w-24 text-right text-xs text-foreground/70 tabular-nums flex-shrink-0">{val}</span>
  </div>
);

const Card = ({ Icon, title, children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    className={`glass frame-pulse rounded-[1.75rem] p-6 sm:p-8 ${className}`}
    style={{ backgroundImage: "radial-gradient(circle at 50% 34%, hsl(28 96% 56% / 0.16), hsl(24 95% 52% / 0.06) 44%, transparent 70%)" }}
  >
    <div className="flex items-center gap-3 mb-5">
      <span className="w-10 h-10 rounded-xl bg-primary/12 border border-primary/35 flex items-center justify-center text-primary flex-shrink-0">
        <Icon size={19} />
      </span>
      <h3 className="font-display text-2xl text-foreground/90">{title}</h3>
    </div>
    {children}
  </motion.div>
);

export function AdoptionLLM() {
  const { i18n } = useTranslation();
  const c = T[(i18n.language || "fr").slice(0, 2)] || T.fr;

  return (
    <section className="relative px-4 md:px-8 py-16 sm:py-24" data-testid="adoption-llm">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-primary/80 tracking-[0.26em] uppercase text-[11px] mb-3">{c.eyebrow}</p>
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-none pb-[0.05em]">{c.title}</h2>
          <p className="mt-5 text-foreground/65 leading-relaxed">{c.intro}</p>
        </div>

        {/* Répartition par âge — cartes encadrées identiques */}
        <Card Icon={Users2} title={c.ageTitle} className="mb-6">
          <p className="text-sm text-foreground/60 mb-5 -mt-2">{c.ageIntro}</p>
          <div className="space-y-3" data-testid="adoption-age-table">
            {c.ageRows.map((r, i) => (
              <div
                key={i}
                className="rounded-xl border border-primary/25 p-4 sm:p-5"
                style={{ backgroundImage: "radial-gradient(circle at 50% 42%, hsl(28 96% 56% / 0.12), hsl(24 95% 52% / 0.04) 46%, transparent 72%)" }}
              >
                <div className="grid sm:grid-cols-[8.5rem_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 sm:gap-6 items-center">
                  <span className="font-display text-lg text-foreground/90 whitespace-nowrap">{r.age}</span>
                  <Bar pct={r.pct} val={r.share} delay={i * 0.06} />
                  <span className="text-sm text-foreground/65 leading-snug">{r.usage}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Géographie */}
          <Card Icon={Globe2} title={c.geoTitle}>
            <p className="text-sm text-foreground/60 mb-5 -mt-2">{c.geoIntro}</p>
            <div className="space-y-4">
              {c.geoRows.map((r, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-foreground/85">{r.label}</span>
                  </div>
                  <Bar pct={r.pct} val={r.val} delay={i * 0.08} />
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs text-foreground/55 leading-relaxed border-l-2 border-primary/30 pl-3">{c.geoNote}</p>
          </Card>

          {/* Plateformes */}
          <Card Icon={PieChart} title={c.platTitle}>
            <div className="space-y-4 mt-1">
              {c.platRows.map((r, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-foreground/85">{r.name}</span>
                  </div>
                  <Bar pct={r.pct} val={r.val} delay={i * 0.08} />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Genre & secteurs */}
        <Card Icon={BarChart3} title={c.genderTitle} className="mt-6">
          <div className="grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-10 items-center">
            <div className="text-center sm:text-left">
              <div className="font-display text-gradient-orange text-5xl leading-none">{c.genderStat}</div>
              <p className="mt-2 text-sm text-foreground/60 max-w-xs">{c.genderLabel}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-primary/70 mb-3">{c.sectorsLabel}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {c.sectors.map((s, i) => (
                  <span key={i} className="text-sm text-foreground/80 px-4 py-3 rounded-xl border border-primary/25 bg-primary/[0.06] flex items-center justify-center text-center leading-snug min-h-[3.5rem]">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <p className="mt-8 text-center text-xs text-foreground/40 italic">{c.disclaimer}</p>
      </div>
    </section>
  );
}
